import { useShallow } from 'zustand/react/shallow';
import { useContextStore } from './useContextStore';

/**
 * DE-702 — latest-results context derivation.
 *
 * A user linked to a consumer experience AND a patient org holds one live
 * formula, generated from the most recent kit order. Only the ACTIVE context's
 * data renders, so a user sitting in the older-kit context sees stale formulas
 * with no signal. This selector compares `lastRnRGeneratedDate` across the two
 * resident context payloads (both are in the store regardless of the active
 * selection) and reports which one is latest and whether the active one is it.
 *
 * Derived on read — NOT persisted. The two payloads are the single source of
 * truth; a stored copy would desync on refetch. Consumed by the experience
 * picker badge and the stale-context banner.
 */

type RnrContext = {
  kitDetails?: { lastRnRGeneratedDate?: string | null } | null;
  CDkitDetails?: { lastRnRGeneratedDate?: string | null } | null;
};

/** ISO-8601 date → epoch ms; null/empty/unparseable → -Infinity (oldest). */
const rnrTime = (d?: string | null): number => {
  if (!d) return -Infinity;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? -Infinity : t;
};

/** A context's effective results time = the later of its two kit dates. */
const contextRnr = (c: RnrContext): number =>
  Math.max(
    rnrTime(c.kitDetails?.lastRnRGeneratedDate),
    rnrTime(c.CDkitDetails?.lastRnRGeneratedDate),
  );

export type LatestContextKey = 'consumer' | number | null;

export interface LatestContext {
  /** Any context has a real `lastRnRGeneratedDate`. */
  hasAnyResults: boolean;
  /** The context holding the latest results; null when none have results. */
  latestContextKey: LatestContextKey;
  /** The active context is the latest (or nobody has results → nothing stale). */
  isActiveLatest: boolean;
}

export const useLatestContext = (): LatestContext =>
  // useShallow: the selector returns a fresh object each call; shallow-compare
  // the fields so React's store subscription doesn't loop on a new reference.
  useContextStore(useShallow((s) => {
    const consumerT = s.consumerInit ? contextRnr(s.consumerInit) : -Infinity;
    // Multi-org is out of scope (DE-702): at most one patient org entry.
    const org = s.patientContent?.entries[0];
    const orgT = org ? contextRnr(org) : -Infinity;

    const globalMax = Math.max(consumerT, orgT);
    const hasAnyResults = globalMax > -Infinity;

    // Consumer wins ties so exactly one card carries the badge. `orgT > consumerT`
    // implies org is finite/defined; fall back to 'consumer' rather than assert.
    const latestContextKey: LatestContextKey = !hasAnyResults
      ? null
      : consumerT >= orgT
        ? 'consumer'
        : (org?.org.id ?? 'consumer');

    // Staleness keys on the active context's OWN time vs the max, not on the
    // tie-broken latestContextKey — so a tie (both at max) is "latest" for
    // whichever context is active, and no false stale banner shows.
    const activeT = s.patientOrg ? orgT : consumerT;

    return {
      hasAnyResults,
      latestContextKey,
      isActiveLatest: !hasAnyResults || activeT >= globalMax,
    };
  }));
